import { ICON_OPTIONS } from '../utils/buttonIcons';
import IconPicker from './IconPicker';

function update(onChange, key, value) {
  onChange((current) => ({ ...current, [key]: value }));
}

function normalizeUrl(value) {
  const trimmed = value.trim();
  if (!trimmed || /^[a-z][a-z\d+.-]*:/i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed.replace(/^\/\//, '')}`;
}

function Field({ label, children, className = '' }) {
  return (
    <label className={`control-field ${className}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <Field label={label} className="color-field">
      <span className="color-input-wrap">
        <input
          type="color"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        <code>{value.toUpperCase()}</code>
      </span>
    </Field>
  );
}

function ButtonControls({
  settings,
  onChange,
  openInNewTab,
  onOpenInNewTabChange
}) {
  const set = (key, value) => update(onChange, key, value);

  return (
    <div className="controls">
      <Field label="Button text">
        <input
          className="text-input"
          value={settings.label}
          maxLength="40"
          onChange={(event) => set('label', event.target.value)}
        />
      </Field>

      <div className="url-option-row">
        <Field label="Button URL">
          <input
            className="text-input"
            type="url"
            value={settings.url}
            placeholder="https://example.com"
            onChange={(event) => set('url', event.target.value)}
            onBlur={(event) => set('url', normalizeUrl(event.target.value))}
          />
        </Field>
        <label className="new-tab-toggle">
          <input
            type="checkbox"
            checked={openInNewTab}
            onChange={(event) => onOpenInNewTabChange(event.target.checked)}
          />
          <span>Open in new tab</span>
        </label>
      </div>

      <div className="icon-picker-field">
        <span className="icon-picker-label">Button icon</span>
        <IconPicker
          value={settings.icon}
          onChange={(value) => set('icon', value)}
        />
        <p className="icon-picker-note">
          Font Awesome 5 must already be installed on your website for the icon
          to show.
        </p>
      </div>

      <Field label="Icon position">
        <select
          className="text-input"
          value={settings.iconPosition}
          onChange={(event) => set('iconPosition', event.target.value)}
          disabled={settings.icon === 'none'}
        >
          <option value="right">Right</option>
          <option value="left">Left</option>
        </select>
      </Field>

      <Field label="Hover interaction" className="hover-field">
        <select
          className="text-input"
          value={settings.hoverEffect}
          onChange={(event) => set('hoverEffect', event.target.value)}
        >
          <option value="lift">Lift</option>
          <option value="grow">Grow</option>
          <option value="glow">Mint glow</option>
          <option value="brighten">Brighten</option>
          <option value="nudge">Nudge right</option>
          <option value="none">No effect</option>
        </select>
      </Field>

      <div className="control-divider"><span>COLORS</span></div>
      <ColorField
        label="Text"
        value={settings.textColor}
        onChange={(value) => set('textColor', value)}
      />

      <div className="toggle-row">
        <span className="toggle-title">Gradient background</span>
        <button
          className={`switch ${settings.gradient ? 'is-on' : ''}`}
          type="button"
          role="switch"
          aria-checked={settings.gradient}
          onClick={() => set('gradient', !settings.gradient)}
        >
          <span />
        </button>
      </div>

      {settings.gradient ? (
        <>
          <div className="two-columns">
            <ColorField
              label="Gradient start"
              value={settings.gradientStart}
              onChange={(value) => set('gradientStart', value)}
            />
            <ColorField
              label="Gradient end"
              value={settings.gradientEnd}
              onChange={(value) => set('gradientEnd', value)}
            />
          </div>
          <Field label="Gradient direction">
            <select
              className="text-input"
              value={settings.gradientDirection}
              onChange={(event) => set('gradientDirection', event.target.value)}
            >
              <option value="90deg">Left to right</option>
              <option value="135deg">Diagonal</option>
              <option value="180deg">Top to bottom</option>
              <option value="45deg">Bottom-left to top-right</option>
            </select>
          </Field>
        </>
      ) : (
        <ColorField
          label="Background"
          value={settings.backgroundColor}
          onChange={(value) => set('backgroundColor', value)}
        />
      )}

      <div className="control-divider"><span>BORDER &amp; SHAPE</span></div>
      <div className="two-columns">
        <Field label={`Border width · ${settings.borderWidth}px`}>
          <input
            type="range"
            min="0"
            max="8"
            value={settings.borderWidth}
            onChange={(event) => set('borderWidth', Number(event.target.value))}
          />
        </Field>
        <Field label={`Corner radius · ${settings.borderRadius}px`}>
          <input
            type="range"
            min="0"
            max="32"
            value={settings.borderRadius}
            onChange={(event) => set('borderRadius', Number(event.target.value))}
          />
        </Field>
      </div>

      <div className="two-columns">
        <Field label="Border style">
          <select
            className="text-input"
            value={settings.borderStyle}
            onChange={(event) => set('borderStyle', event.target.value)}
          >
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
            <option value="dotted">Dotted</option>
          </select>
        </Field>
        <ColorField
          label="Border color"
          value={settings.borderColor}
          onChange={(value) => set('borderColor', value)}
        />
      </div>

      <div className="control-divider">
        <span>TYPOGRAPHY &amp; SPACING</span>
      </div>
      <div className="format-row">
        <button
          type="button"
          className={`format-button ${settings.bold ? 'selected' : ''}`}
          aria-pressed={settings.bold}
          onClick={() => set('bold', !settings.bold)}
        >
          <b>B</b> Bold
        </button>
        <button
          type="button"
          className={`format-button ${settings.italic ? 'selected' : ''}`}
          aria-pressed={settings.italic}
          onClick={() => set('italic', !settings.italic)}
        >
          <i>I</i> Italic
        </button>
      </div>

      <div className="two-columns">
        <Field label={`Vertical padding · ${settings.paddingVertical}px`}>
          <input
            type="range"
            min="4"
            max="28"
            value={settings.paddingVertical}
            onChange={(event) =>
              set('paddingVertical', Number(event.target.value))
            }
          />
        </Field>
        <Field label={`Horizontal padding · ${settings.paddingHorizontal}px`}>
          <input
            type="range"
            min="8"
            max="48"
            value={settings.paddingHorizontal}
            onChange={(event) =>
              set('paddingHorizontal', Number(event.target.value))
            }
          />
        </Field>
      </div>
    </div>
  );
}

export default ButtonControls;